import { useQuery } from '@tanstack/react-query'
import { Button, ButtonLoading } from '@tovala/component-library'
import { clsx } from 'clsx'
import { useEffect, useState } from 'react'
import {
  Control,
  FieldErrors,
  useForm,
  UseFormRegister,
  useWatch,
} from 'react-hook-form'
import { useLocation } from 'react-router-dom'

import { events } from 'analytics/events'
import APIErrorDisplay from 'components/common/APIErrorDisplay'
import { useCreateMealReviews } from 'components/core/hooks/reviews'
import { reviewQueries } from 'components/core/queries/reviews'
import { RatingChip } from 'components/core/service/combinedapi/reviews'
import { useUser } from 'contexts/user'
import { track } from 'utils/analytics'
import { getMealImageURL } from 'utils/meals'

import { useAutoCloseAfterSubmit } from './hooks'
import { RatingDialogCloseResult } from './MealRatingDialog'
import RatingDialog, { RatingDialogCloseButton } from './RatingDialog'
import RatingSubmittedCard from './RatingSubmittedCard'
import {
  MealReviewFormValues,
  RatingChipOptions,
  RatingStars,
  ReviewCommentField,
  SharePubliclyToggle,
} from './ReviewFields'
import {
  CREATE_REVIEW_ERRORS,
  getCookedDayLabel,
  getCreateMealReviewData,
  getLastSharePubliclyChoice,
  getRatingChipsForRating,
  getReviewSourceID,
  joinMealIDs,
  LOAD_RATING_CHIPS_ERRORS,
  MealToRate,
  NEUTRAL_RATING,
  REVIEW_COMMENT_RULES,
  saveSharePubliclyChoice,
} from './utils'

type ReviewKey = `meal${number}`

interface FormData {
  reviews: Record<ReviewKey, MealReviewFormValues>
  sharePublicly: boolean
}

function getReviewKey(mealID: number): ReviewKey {
  // A purely numeric key would make react-hook-form treat `reviews` as an array.
  return `meal${mealID}`
}

const MultiMealRatingDialog = ({
  mealsToRate: initialMealsToRate,
  onClose,
  sourceID,
}: {
  mealsToRate: MealToRate[]
  onClose(result: RatingDialogCloseResult): void
  sourceID?: string
}) => {
  const { pathname } = useLocation()
  const { user } = useUser()
  const [mealsToRate] = useState(initialMealsToRate)
  const [now] = useState(() => new Date())
  const reviewSourceID = getReviewSourceID({ pathname, sourceID })

  const {
    data: ratingChips,
    error: loadRatingChipsError,
    isError: hasLoadRatingChipsError,
  } = useQuery(reviewQueries.ratingChips())

  const [expandedMealID, setExpandedMealID] = useState<number | null>(null)
  const [hasSubmittedAllReviews, setHasSubmittedAllReviews] = useState(false)
  const [savedMealIDs, setSavedMealIDs] = useState<ReadonlySet<number>>(
    () => new Set(),
  )

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      reviews: Object.fromEntries(
        mealsToRate.map(({ meal }) => [
          getReviewKey(meal.id),
          { comment: '', rating: '', selectedChipIDs: [] },
        ]),
      ),
      sharePublicly: getLastSharePubliclyChoice(user.id),
    },
  })

  const selectedRatings = useWatch({
    control,
    name: mealsToRate.map(
      ({ meal }) => `reviews.${getReviewKey(meal.id)}.rating` as const,
    ),
  })
  const hasRatedMeal = selectedRatings.some(Boolean)
  const sharePublicly = useWatch({ control, name: 'sharePublicly' })

  const getMealIDsInOrder = (mealIDs: ReadonlySet<number>) =>
    mealsToRate
      .map(({ meal }) => meal.id)
      .filter((mealID) => mealIDs.has(mealID))

  const trackReviewsCompleted = (mealIDs: ReadonlySet<number>) => {
    track(events.ORDERS_MEAL_REVIEW_COMPLETED, {
      meal_id: joinMealIDs(getMealIDsInOrder(mealIDs)),
      source_id: reviewSourceID,
    })
  }

  const {
    error: createMealReviewsError,
    isError: hasCreateMealReviewsError,
    isPending: isCreatingMealReviews,
    mutate: createMealReviews,
  } = useCreateMealReviews({
    onMealReviewCreated: (data) => {
      setSavedMealIDs((savedMealIDs) => new Set(savedMealIDs).add(data.mealid))
      saveSharePubliclyChoice({
        sharePublicly: data.sharePublicly ?? true,
        userID: user.id,
      })
    },
    // Tracked here rather than in mutate's callbacks so it still fires when
    // the user closes the dialog while the reviews are being submitted.
    onSuccess: (_data, { reviews }) => {
      trackReviewsCompleted(
        new Set([
          ...Array.from(savedMealIDs),
          ...reviews.map((review) => review.mealid),
        ]),
      )
      setHasSubmittedAllReviews(true)
    },
  })

  useEffect(() => {
    track(events.ORDERS_MEAL_REVIEW_SHOWN, {
      meal_id: joinMealIDs(mealsToRate.map(({ meal }) => meal.id)),
      source_id: reviewSourceID,
    })
  }, [mealsToRate, reviewSourceID])

  const closeAfterSubmit = () => {
    onClose({ wasRated: true })
  }

  useAutoCloseAfterSubmit({
    hasSubmitted: hasSubmittedAllReviews,
    onAutoClose: closeAfterSubmit,
  })

  const handleDismiss = () => {
    if (isCreatingMealReviews) {
      onClose({ wasRated: true })
      return
    }

    if (savedMealIDs.size > 0) {
      trackReviewsCompleted(savedMealIDs)
    } else {
      track(events.ORDERS_MEAL_REVIEW_DISMISSED, {
        meal_id: joinMealIDs(mealsToRate.map(({ meal }) => meal.id)),
        source_id: reviewSourceID,
      })
    }

    onClose({ wasRated: savedMealIDs.size > 0 })
  }

  const handleReviewsSubmit = (values: FormData) => {
    const reviews = mealsToRate.flatMap(({ meal }) => {
      const review = values.reviews[getReviewKey(meal.id)]
      if (!review?.rating || savedMealIDs.has(meal.id)) {
        return []
      }

      return [
        getCreateMealReviewData({
          chips: ratingChips?.chips ?? [],
          comment: review.comment,
          mealID: meal.id,
          rating: Number(review.rating),
          selectedChipIDs: review.selectedChipIDs,
          sharePublicly: values.sharePublicly,
          userID: user.id,
        }),
      ]
    })

    track(events.REVIEW_SUBMITTED, {
      meal_id: joinMealIDs(reviews.map((review) => review.mealid)),
      source_id: reviewSourceID,
    })

    createMealReviews({ reviews, userID: user.id })
  }

  const expandFirstInvalidReview = (errors: FieldErrors<FormData>) => {
    const firstInvalidMealToRate = mealsToRate.find(
      ({ meal }) => errors.reviews?.[getReviewKey(meal.id)],
    )
    if (firstInvalidMealToRate) {
      setExpandedMealID(firstInvalidMealToRate.meal.id)
    }
  }

  if (hasSubmittedAllReviews) {
    return (
      <RatingDialog onClose={closeAfterSubmit} size="multiMeal">
        <div className="absolute right-6 top-[26px] md:right-4 md:top-4">
          <RatingDialogCloseButton
            buttonStyle="orange"
            onClose={closeAfterSubmit}
          />
        </div>
        <RatingSubmittedCard dialogSize="multiMeal" rating={NEUTRAL_RATING} />
      </RatingDialog>
    )
  }

  return (
    <RatingDialog onClose={handleDismiss} size="multiMeal">
      <form
        className="flex max-h-[calc(100dvh-1rem)] flex-col"
        onSubmit={handleSubmit(handleReviewsSubmit, expandFirstInvalidReview)}
      >
        <div className="flex shrink-0 items-center justify-between gap-4 px-6 pb-9 pt-6 md:flex-col-reverse md:items-stretch md:gap-3 md:px-4 md:pb-4 md:pt-4">
          <h2 className="text-k/26_110 md:text-k/24_120">
            How were your recent meals?
          </h2>
          <div className="flex shrink-0 justify-end">
            <RatingDialogCloseButton
              buttonStyle="orange"
              onClose={handleDismiss}
            />
          </div>
        </div>

        <div className="relative min-h-0 flex-auto overflow-y-auto">
          <div className="space-y-4 px-6 md:space-y-6 md:px-4">
            {mealsToRate.map(({ cookedAt, meal }, index) => (
              <div
                key={meal.id}
                className={clsx({
                  'border-t border-grey-4 pt-4 md:border-none md:pt-0':
                    index > 0,
                })}
              >
                <MultiMealRatingRow
                  control={control}
                  cookedDayLabel={
                    cookedAt ? getCookedDayLabel({ cookedAt, now }) : null
                  }
                  errors={errors}
                  hasLoadRatingChipsError={hasLoadRatingChipsError}
                  imageURL={getMealImageURL(meal)}
                  isExpanded={expandedMealID === meal.id}
                  isSaved={savedMealIDs.has(meal.id)}
                  loadRatingChipsError={loadRatingChipsError}
                  mealID={meal.id}
                  onEdit={() => setExpandedMealID(meal.id)}
                  onRatingChange={() => setExpandedMealID(meal.id)}
                  onToggleSharePublicly={() => {
                    setValue('sharePublicly', !sharePublicly)
                  }}
                  ratingChips={ratingChips?.chips ?? []}
                  register={register}
                  sharePublicly={sharePublicly}
                  sourceID={reviewSourceID}
                  title={meal.title}
                />
              </div>
            ))}
          </div>

          <div className="px-6 pt-4 md:px-4 md:pb-6 md:pt-6">
            <div className="border-t border-grey-4 pt-4 md:border-none md:pt-0">
              <SharePubliclyToggle
                id="multi-meal-share-publicly"
                registration={register('sharePublicly')}
                sharePublicly={sharePublicly}
              />
            </div>
          </div>
        </div>

        <div className="shrink-0 space-y-4 px-6 py-6 md:px-4 md:py-3">
          {hasCreateMealReviewsError && (
            <APIErrorDisplay
              error={createMealReviewsError}
              errorCodeMessageMap={CREATE_REVIEW_ERRORS}
            />
          )}

          <div className="flex justify-center">
            {hasRatedMeal ? (
              <ButtonLoading
                isLoading={isCreatingMealReviews}
                size="large"
                type="submit"
              >
                Submit
              </ButtonLoading>
            ) : (
              <Button onClick={handleDismiss} size="large" type="button">
                Dismiss
              </Button>
            )}
          </div>
        </div>
      </form>
    </RatingDialog>
  )
}

export default MultiMealRatingDialog

const MultiMealRatingRow = ({
  control,
  cookedDayLabel,
  errors,
  hasLoadRatingChipsError,
  imageURL,
  isExpanded,
  isSaved,
  loadRatingChipsError,
  mealID,
  onEdit,
  onRatingChange,
  onToggleSharePublicly,
  ratingChips,
  register,
  sharePublicly,
  sourceID,
  title,
}: {
  control: Control<FormData>
  cookedDayLabel: string | null
  errors: FieldErrors<FormData>
  hasLoadRatingChipsError: boolean
  imageURL: string
  isExpanded: boolean
  isSaved: boolean
  loadRatingChipsError: Error | null
  mealID: number
  onEdit(): void
  onRatingChange(): void
  onToggleSharePublicly(): void
  ratingChips: RatingChip[]
  register: UseFormRegister<FormData>
  sharePublicly: boolean
  sourceID: string
  title: string
}) => {
  const reviewKey = getReviewKey(mealID)
  const review = useWatch({
    control,
    name: `reviews.${reviewKey}`,
  })
  const selectedRating = review?.rating ? Number(review.rating) : 0
  const chipsForRating = selectedRating
    ? getRatingChipsForRating({ chips: ratingChips, rating: selectedRating })
    : []

  return (
    <div aria-label={title} className="space-y-4" role="group">
      <div className="flex gap-4 md:gap-3">
        <div className="h-[83px] w-[83px] shrink-0 overflow-hidden rounded md:h-[97px] md:w-[97px]">
          {imageURL ? (
            <img
              alt=""
              className="h-full w-full object-cover"
              src={`https://${imageURL}`}
            />
          ) : (
            <div className="h-full w-full bg-grey-3" />
          )}
        </div>

        <div className="flex min-w-0 grow flex-col justify-between gap-3">
          <div className="space-y-1">
            <p className="text-k/18_120 md:text-k/16_125">{title}</p>
            {cookedDayLabel && (
              <p className="text-k/14_120 text-grey-10">{cookedDayLabel}</p>
            )}
          </div>

          <div className="flex items-center gap-4">
            <fieldset
              className={clsx({ 'pointer-events-none': isSaved })}
              disabled={isSaved}
            >
              <RatingStars
                idPrefix={`multi-${mealID}`}
                mealID={mealID}
                registration={register(`reviews.${reviewKey}.rating`, {
                  onChange: onRatingChange,
                })}
                selectedRating={selectedRating}
                size="compact"
                sourceID={sourceID}
              />
            </fieldset>

            {isSaved ? (
              <p className="text-k/16_125 text-grey-10">Saved</p>
            ) : (
              selectedRating > 0 &&
              !isExpanded && (
                <button
                  aria-label={`Edit review for ${title}`}
                  className="text-k/16_125 text-orange-2"
                  onClick={onEdit}
                  type="button"
                >
                  Edit
                </button>
              )
            )}
          </div>
        </div>
      </div>

      {selectedRating > 0 && (
        // Collapsed reviews stay mounted so react-hook-form still validates
        // their comments on submit.
        <div className="space-y-4" hidden={!isExpanded || isSaved}>
          {hasLoadRatingChipsError ? (
            <APIErrorDisplay
              error={loadRatingChipsError}
              errorCodeMessageMap={LOAD_RATING_CHIPS_ERRORS}
            />
          ) : (
            chipsForRating.length > 0 && (
              <RatingChipOptions
                chips={chipsForRating}
                idPrefix={`multi-${mealID}`}
                mealID={mealID}
                registration={register(`reviews.${reviewKey}.selectedChipIDs`)}
                selectedChipIDs={review?.selectedChipIDs ?? []}
              />
            )
          )}

          <ReviewCommentField
            errorMessage={errors.reviews?.[reviewKey]?.comment?.message}
            id={`multi-${mealID}-comment`}
            onToggleSharePublicly={onToggleSharePublicly}
            registration={register(
              `reviews.${reviewKey}.comment`,
              REVIEW_COMMENT_RULES,
            )}
            sharePublicly={sharePublicly}
          />
        </div>
      )}
    </div>
  )
}
